// src/utils/recommendations.ts
import { BlogPostMetadata } from '../data/blogData';

interface PostEmbedding {
  slug: string;
  embedding: number[];
}

/**
 * Calculate cosine similarity between two vectors
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (!a.length || !b.length || a.length !== b.length) return 0;
  
  let dotProduct = 0;
  let aMagnitude = 0; 
  let bMagnitude = 0;
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    aMagnitude += a[i] * a[i];
    bMagnitude += b[i] * b[i];
  }
  
  aMagnitude = Math.sqrt(aMagnitude);
  bMagnitude = Math.sqrt(bMagnitude);
  
  if (aMagnitude === 0 || bMagnitude === 0) return 0;
  
  return dotProduct / (aMagnitude * bMagnitude);
}

/**
 * Get related posts based on embedding similarity
 */
export async function getRelatedPosts(
  currentPost: BlogPostMetadata,
  allPosts: BlogPostMetadata[],
  limit: number = 3
): Promise<BlogPostMetadata[]> {
  try {
    // Load embeddings from the static file
    const response = await fetch('/data/embeddings.json');
    if (!response.ok) throw new Error('Failed to load embeddings');
    const embeddings: PostEmbedding[] = await response.json();
    
    // Find current post's embedding
    const currentEmbedding = embeddings.find(e => e.slug === currentPost.slug)?.embedding;
    if (!currentEmbedding || !currentEmbedding.length) return [];
    
    // Calculate similarity scores
    const postsWithScores = allPosts
      .filter(post => post.slug !== currentPost.slug) // Exclude current post
      .map(post => {
        const postEmbedding = embeddings.find(e => e.slug === post.slug)?.embedding;
        if (!postEmbedding) return { post, score: 0 };
        
        const similarity = cosineSimilarity(currentEmbedding, postEmbedding);
        return { post, score: similarity };
      })
      .filter(item => item.score > 0.5) // Only include reasonably similar posts
      .sort((a, b) => b.score - a.score) // Sort by similarity (descending)
      .slice(0, limit); // Take only the most similar ones
      
    return postsWithScores.map(item => item.post);
  } catch (error) {
    console.error('Error getting related posts:', error);
    return [];
  }
}
